import * as Yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum ReflorPurchaseOrderLineItemKeys {
  itemNumber = 'itemNumber',
  pattern = 'pattern',
  style = 'style',
  color = 'color',
  size = 'size',
  quantity = 'quantity',
  unit = 'unit',
  cost = 'cost',
  totalCost = 'totalCost',
}

const ReflorLineItemSchema = Yup.object().shape({
  [ReflorPurchaseOrderLineItemKeys.itemNumber]: Yup.number().nullable().optional(),
  [ReflorPurchaseOrderLineItemKeys.pattern]: Yup.string().nullable().optional(),
  [ReflorPurchaseOrderLineItemKeys.style]: Yup.string().nullable().optional(),
  [ReflorPurchaseOrderLineItemKeys.color]: Yup.string().nullable().optional(),
  [ReflorPurchaseOrderLineItemKeys.size]: Yup.string().nullable().optional(),
  [ReflorPurchaseOrderLineItemKeys.quantity]: Yup.string().nullable().optional(),
  [ReflorPurchaseOrderLineItemKeys.unit]: Yup.string().nullable().optional(),
  [ReflorPurchaseOrderLineItemKeys.cost]: Yup.string().nullable().optional(),
  [ReflorPurchaseOrderLineItemKeys.totalCost]: Yup.string().nullable().optional(),
})

export const reflorPurchaseOrderFileDataSchema = Yup.object().shape({
  organizationLogoOverride: Yup.string().nullable().optional(),
  contact: Yup.object()
    .shape({
      name: Yup.string().nullable().optional(),
      phoneNumber: Yup.string().nullable().optional(),
    })
    .required(),
  organization: Yup.object()
    .shape({
      companyName: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      addressLine2: Yup.string().nullable().optional(),
      cityStateZip: Yup.string().nullable().optional(),
      phoneNumber: Yup.string().nullable().optional(),
      website: Yup.string().nullable().optional(),
    })
    .required(),
  vendor: Yup.object()
    .shape({
      vendorId: Yup.string().nullable().optional(),
      companyName: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      addressLine2: Yup.string().nullable().optional(),
      cityStateZip: Yup.string().nullable().optional(),
      faxCode: Yup.string().nullable().optional(),
      emailAddress: Yup.string().nullable().optional(),
    })
    .required(),
  purchaseOrderNumber: Yup.string().nullable().optional(),
  proposalNumber: Yup.string().nullable().optional(),
  accountNumber: Yup.string().nullable().optional(),
  date: Yup.string().nullable().optional(),
  deliveryDate: Yup.string().nullable().optional(),
  referenceNum: Yup.string().nullable().optional(),
  reserveNum: Yup.string().nullable().optional(),
  quoteNum: Yup.string().nullable().optional(),
  destination: Yup.object()
    .shape({
      company: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      addressLine2: Yup.string().nullable().optional(),
      cityStateZip: Yup.string().nullable().optional(),
      contactName: Yup.string().nullable().optional(),
      contactFullName: Yup.string().nullable().optional(),
      contactEmail: Yup.string().nullable().optional(),
      contactPhone: Yup.string().nullable().optional(),
    })
    .required(),
  items: Yup.array().of(ReflorLineItemSchema).required(),
  note1: Yup.string().nullable().optional(),
  note2: Yup.string().nullable().optional(),
  taxMessage: Yup.string().nullable().optional(),
})

export type ReflorPurchaseOrderFileData = Yup.InferType<typeof reflorPurchaseOrderFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<ReflorPurchaseOrderFileData>

export const reflorPurchaseOrderFileMappings: RequiredFileData = {
  organizationLogoOverride: 'e08e941d-fbf1-4d28-adf2-d0a36c2308cc',
  contact: {
    name: '6ee48bbe-96e1-4b4c-8739-21109064ead0',
    phoneNumber: '002f04bc-0e15-4ce2-9a15-3d5bad7d6564',
  },
  organization: {
    companyName: 'b32fc074-7394-460e-bb83-acd4da8e1baa',
    addressLine1: '19c590d2-eceb-44d2-bdd7-675af7b810c2',
    addressLine2: '0ca3209b-11bd-44af-bf87-d67a53f5657a',
    cityStateZip: '29b31ac9-0c11-44fe-86bf-b264413f61b5',
    phoneNumber: '8f85e07c-ae82-4c75-a57e-ac8a7aeb342a',
    website: '08d23bc6-c6a7-4415-a0f0-a85f0303804b',
  },
  vendor: {
    vendorId: '93098bf9-aad5-47f9-977f-5c4706f178df',
    companyName: '6dcdacce-64fb-4dd2-ab62-d4bda703c175',
    addressLine1: 'a2a0845d-a09e-49a0-b340-7e72cd030da9',
    addressLine2: '4172a6c0-536a-473f-8797-d69e37175910',
    cityStateZip: '97033dab-588d-40d0-9083-7e7a8b24b5af',
    faxCode: '37bce413-58ed-4053-b537-7cf6cbcc7ac2',
    emailAddress: 'bbdce1a9-429a-4187-9c19-51764af35cbc',
  },
  purchaseOrderNumber: '06fbee2c-e611-4036-8790-90d85c179936',
  proposalNumber: '76acde4c-3da1-421b-8bc8-829eb2e3964e',
  accountNumber: '7a00b554-911a-4784-8476-0276cedd17d8',
  date: 'ce60a45e-f648-4360-9b39-ad54018d93a7',
  referenceNum: 'a484553b-ccb5-4783-bd37-471b91b29b40',
  reserveNum: 'aa27c1b3-efd7-40d7-99ee-96e5ac3032c3',
  quoteNum: '0250ef2b-1072-4caf-97c8-f0c49a486b10',
  deliveryDate: '1a8137ee-ad86-4d86-9f4e-e5beeab4abc5',
  destination: {
    company: '2761bc9e-8307-415d-aaed-b4839f505cf3',
    addressLine1: '065d32e6-0e01-4571-a69c-73b460ff03e9',
    addressLine2: 'f696d16e-7ff3-4d71-b81b-3c13a14ef5e8',
    cityStateZip: '4ee0e075-88ce-4538-83ca-e7ed959dd10c',
    contactName: 'b0dd460b-b366-4157-8172-2fe5752e77d0',
    contactFullName: 'bb2550f1-911a-4943-9bb0-7757ad35ce9f',
    contactEmail: '59f0f574-0dd2-41e5-935b-b81482094216',
    contactPhone: '5fbc3b22-4a0e-4cbd-84c6-f837f7a9500b',
  },
  note1: '13f85daa-9af7-4191-801d-ac35b14aa8a4',
  note2: '32cf3533-d4fc-4124-8f5e-3a55f3dfffee',
  taxMessage: 'e9cfcf3c-cf16-4923-bb07-2d16c75f0dd6',
  items: [
    {
      itemNumber: null,
      pattern: '501d82a6-c593-4d29-be33-76686ca78987',
      style: '5f8a1bf8-5696-494f-bf36-571f00f44841',
      color: 'f86b32d7-d021-49ee-900e-24c9d61fc59a',
      size: '3995733d-7354-4360-87b2-94f6e4d6b5fc',
      quantity: 'b00f4dc1-91c1-4da4-b737-ea64a98fb408',
      unit: '082c96c1-cb03-48de-bc9b-af9f3d63001f',
      cost: 'c5bb2fef-51f3-4632-b761-5008830ddc58',
      totalCost: null,
    },
  ],
}
